import { HIDDEN_CLASS, INVALID_CLASS } from '../constants';
import { addClass, queryAll, queryFirst, removeClass } from '../domUtil';
import { formatPhoneNumber } from '../util';

const LS_PAGES_VISITED = 'lightbox_emailcapture_pages';
const LS_DISMISSED = 'lightbox_emailcapture_dismissed';
const LS_SUBMITTED = 'lightbox_emailcapture_submitted';

const dialog = queryFirst('#email-flyin-v1');
const emailForm = queryFirst('.js-flyin-email-form', dialog);
const emailInput = queryFirst('.js-flyin-email-input', dialog);
const mobileInput = queryFirst('.js-flyin-mobile-input', dialog);
const errorMessages = queryFirst('.js-flyin-email-errors', dialog);
const mobileErrorMessages = queryFirst('.flyin-mobile-number-errors', dialog);
const subscribeButton = queryFirst('.js-email-submit-button', dialog);
const formElem = queryFirst('.js-email-lightbox-form', dialog);
const thanksElem = queryFirst('.js-email-subscribe-thanks', dialog);

function formatNumberHandler () {
    return formatPhoneNumber(mobileInput);
}
/**
* Closes the email sign-up lightbox
*/
function closeLightbox() {
    localStorage.setItem(LS_DISMISSED, true);
    emailForm.removeEventListener('submit', submitHandler);

    if (mobileInput) {
        mobileInput.removeEventListener('keyup', formatNumberHandler);
    }
}

/**
 * Checks whether the current page in on the excluded pages list (meaning the lghtbox won't display)
 * @param {...string} [additionalPages] - any additional page strings to check
 * @returns {boolean} whether the current page is on the excluded pages list
 */
function checkLightboxExcludedPages(...additionalPages) {
    const { location } = document;
    const excludedPages = ['/club-lilly/', '/register/', '/login/', '/help/', '/account/', '/contact-us/', '/cart/', '/checkout/', ...additionalPages];
    return excludedPages.some(page => location.href.includes(page));
}

/**
 * Checks whether the user arrived on the current page by clicking a link in an SFMC email
 * @returns {boolean} whether the user arrived using an SFMC link
 */
function arrivedViaEmailLink() {
    const { location } = document;
    const qsParams = new URLSearchParams(location.search);
    return qsParams.get('utm_source') === 'sfmc';
}

/**
 * Submits a subscription request
 * @param {string} url - the endpoint to submit to
 * @param {string} email - the email to subscribe
 * @param {string} [source] - the source of the subscription
 * @param {string} [mobile] - the mobile number to subscribe
 * @returns {Object} response data object
 */
async function submitSubscription(url, email, source, mobile) {
    const formData = new FormData();
    formData.append('emailId', email);
    if (source) formData.append('source', source);
    if (mobile) formData.append('mobileNum', mobile);

    const response = await fetch(url, {
        method: 'POST',
        body: formData
    });

    if (!response.ok) {
        console.error(`emailLightbox: subscribe response was not OK (HTTP ${response.status})`);
        return {
            success: false,
            msg: 'Something went wrong, please try again'
        };
    }

    return await response.json();
}

/**
 * Displays an error message on the form
 * @param {string} message - The error message to be displayed
 * @returns {boolean} false
 */
function showError(response) {
    if (errorMessages && response.emailExists) {
        errorMessages.textContent = response.msg;
        addClass(emailInput, INVALID_CLASS);
    }
    if (mobileErrorMessages && response.phoneExists) {
        mobileErrorMessages.textContent = response.msg;
        addClass(mobileInput, INVALID_CLASS);
    }
    return false;
}

/**
 * Shows a success message to the user
 */
function showSuccess() {
    localStorage.setItem(LS_SUBMITTED, true);

    if (formElem && thanksElem) {
        addClass(formElem, HIDDEN_CLASS);
        removeClass(thanksElem, HIDDEN_CLASS);
        thanksElem.setAttribute('role', 'alert');
    }
}

/**
 * Displays a message to the user based on the form submission response
 * @param {Object} response - form submission response JSON object
 */
function displayMessage(response) {
    const $body = $(document.body);

    $.spinner().stop();
    if (response.success) {
        showSuccess();
        $body.trigger('subscribe:success');
    } else {
        showError(response);
        $body.trigger('subscribe:fail');
    }
    subscribeButton.disabled = false;
    subscribeButton.removeAttribute('aria-disabled');
}

async function submitHandler(e) {
    e.preventDefault();
    const emailId = emailInput.value;
    const mobileId = mobileInput ? mobileInput.value : '';
    const { href: subscribeUrl } = subscribeButton.dataset;
    const subSource = window.LP_EmailSignup_Source || 'promoLightbox';

    if (!emailId) return showError({ msg: emailInput.dataset.missingError, emailExists: true });
    if (!mobileId) return showError({ msg: mobileId.dataset.missingError, phoneExists: true });
    if (!subscribeUrl) {
        console.error('emailLightbox: no subscription URL found on button');
        return false;
    }

    emailInput.classList.remove(INVALID_CLASS);
    subscribeButton.disabled = true;
    subscribeButton.setAttribute('aria-disabled', 'true');
    $.spinner().start();

    const data = await submitSubscription(subscribeUrl, emailId, subSource, mobileId);

    displayMessage(data);
}

/**
 * Initializes email lightbox event listeners
 * @param {boolean} [forceInit=false] - forces the lightbox to show on-demand on non-excluded pages.
 */
function initEmailLightbox(forceInit = false) {
    let pagesVisited = parseInt(localStorage.getItem(LS_PAGES_VISITED), 10) || 0;
    const { referrer } = document;
    const isLandingPage = !referrer.includes('lillypulitzer.com') && !referrer.includes('.dx.');
    const isRefferedFromAnotherUrl = referrer !== window.location.href;
    const isExcludedPage = checkLightboxExcludedPages();
    const isFromSFMC = arrivedViaEmailLink();

    if (isFromSFMC) {
        // if this parameter exists, the user got here from an email link --
        // set submitted to true in case they are on a different device
        localStorage.setItem(LS_SUBMITTED, true);
    }

    const hasDismissed = localStorage.getItem(LS_DISMISSED) === 'true';
    const hasSubmitted = localStorage.getItem(LS_SUBMITTED) === 'true';

    if (isExcludedPage || (!forceInit && (hasDismissed || hasSubmitted))) return;

    if (forceInit || !isLandingPage && isRefferedFromAnotherUrl) {
        localStorage.setItem(LS_PAGES_VISITED, ++pagesVisited);

        if (forceInit || pagesVisited >= 1) {
            if (!dialog || !emailForm || !emailInput || !errorMessages || !subscribeButton) return;

            const smsEnabled = queryFirst('.data-signup-sms-enabled');
            if (!smsEnabled) {
                addClass(queryFirst('.enable-sms-capture'), HIDDEN_CLASS);
            }

            if (mobileInput) {
                mobileInput.addEventListener('keyup', formatNumberHandler);
            }

            emailForm.addEventListener('submit', submitHandler);

            const $dialog = $(dialog);

            const focusableSel = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]';
            function trapKeydown(e) {
                if (e.key !== 'Tab') return;
                const $f = $dialog.find(focusableSel).filter(':visible');
                if (!$f.length) return;
                const first = $f[0];
                const last  = $f[$f.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === first) { e.preventDefault(); last.focus(); }
                } else {
                    if (document.activeElement === last) { e.preventDefault(); first.focus(); }
                }
            }

            $dialog
                .on('shown.bs.modal', () => {
                    $dialog.on('keydown.trapFocus', trapKeydown);
                    (emailInput || mobileInput)?.focus();
                })
                .on('hidden.bs.modal', () => {
                    $dialog.off('keydown.trapFocus');
                    closeLightbox();
                });

            (function setupA11yValidation() {
                const pairs = [
                    { input: emailInput,  err: errorMessages },
                    { input: mobileInput, err: mobileErrorMessages }
                ];

                let firstInvalidFocused = false;

                emailForm.addEventListener('submit', () => { firstInvalidFocused = false; }, true);

                pairs.forEach(({ input, err }) => {
                    if (!input || !err) return;

                    input.addEventListener('input', () => {
                        input.removeAttribute('aria-invalid');
                        input.setCustomValidity('');
                    });


                    input.addEventListener('invalid', (e) => {
                        e.preventDefault();
                        const v = input.validity;
                        let msg = '';

                        if (v.valueMissing) {
                            msg = input.dataset.missingError || 'This field is required';
                        } else if (v.tooLong) {
                            msg = input.dataset.rangeError || 'Value is too long';
                        } else if (v.typeMismatch || v.patternMismatch) {
                            msg = input.dataset.patternMismatch || 'Please enter a valid value';
                        }

                        input.setCustomValidity(msg);
                        input.setAttribute('aria-invalid', 'true');
                        err.textContent = msg;

                        if (!firstInvalidFocused) {
                            input.focus();
                            firstInvalidFocused = true;
                        }
                    });
                });
            })();

            $dialog.modal('show');
        }
    }
}

export {
    initEmailLightbox,
    checkLightboxExcludedPages,
    arrivedViaEmailLink,
    submitSubscription
};
